import { INTERACTION, INTERACTION_PROPS, LAYER, LAYER_PROPS, LAYER_ZINDEX, MEASURE_LABEL_STYLE, MEASURE_MODIFY_STYLE, MEASURE_SEGMENT_STYLE, MEASURE_STYLE } from 'constants/Constants';
import { primaryAction } from "ol/events/condition";
import { Geometry, LineString, Point } from 'ol/geom';
import Polygon from 'ol/geom/Polygon';
import { Modify } from "ol/interaction";
import Draw from 'ol/interaction/Draw.js';
import Layer from 'ol/layer/Layer';
import VectorLayer from "ol/layer/Vector";
import VectorSource from 'ol/source/Vector';
import { getArea, getLength } from 'ol/sphere';
import { mapStore } from 'stores/Map';
import { getInteractionByProps, getLayerByProps } from 'utils/OlUtil';
import { setStrLocaleString } from "utils/Utils";

export const useMapMeasure = () => {
    const { map, measureTipPoint } = mapStore();

    const getDraw = () => {
        if (!map) return null;
        return getInteractionByProps(map, INTERACTION.DRAW);
    }

    const getModify = () => {
        if (!map) return null;
        return getInteractionByProps(map, INTERACTION.MODIFY);
    }

    const formatLength = (line: LineString): string => {
        const length = getLength(line);
        return length > 100 ? `${setStrLocaleString(Math.round((length / 1000) * 100) / 100)} km` : `${setStrLocaleString(Math.round(length * 100) / 100)} m`;
    };
     
    const formatArea = (polygon: Polygon): string => {
        const area = getArea(polygon);
        return `${setStrLocaleString(Math.round(area / 10000) / 100)} km²`;
    };
    
    const segmentStyles = [MEASURE_SEGMENT_STYLE];
    const measureStyleFunction = (feature: any, segments: boolean, drawType?: string, tip?: string) => {
        const styles = [];
        const geometry = feature.getGeometry();
        const type = geometry.getType();
        let point, label, line;

        if (!drawType || drawType === type || type === 'Point') {
            styles.push(MEASURE_STYLE);
            if (type === 'Polygon') {
                point = geometry.getInteriorPoint();
                label = formatArea(geometry);
                line = new LineString(geometry.getCoordinates()[0]);
            } else if (type === 'LineString') {
                point = new Point(geometry.getLastCoordinate());
                label = formatLength(geometry);
                line = geometry;
            }
        }

        if (segments && line) {
            let count = 0;
            line.forEachSegment((a: [number, number], b: [number, number]) => {
                const segment = new LineString([a, b]);
                const label = formatLength(segment);
                if (segmentStyles.length - 1 < count) {
                    segmentStyles.push(MEASURE_SEGMENT_STYLE.clone());
                }
                const segmentPoint = new Point(segment.getCoordinateAt(0.5));
                segmentStyles[count].setGeometry(segmentPoint);
                segmentStyles[count].getText()?.setText(label);
                styles.push(segmentStyles[count]);
                count++;
            });
        }
        
        if (label) {
            MEASURE_LABEL_STYLE.setGeometry(point);
            MEASURE_LABEL_STYLE.getText()?.setText(label);
            styles.push(MEASURE_LABEL_STYLE);
        }
        
        return styles;
    }

    const clearMeasureInteractions = () => {
        map?.removeInteraction(getDraw() as Draw);
        map?.removeInteraction(getModify() as Modify);
    }

    const clearLayers = (layers: Layer[]) => {
        layers.forEach((layer) => {
            (layer.getSource() as VectorSource).clear();
            layer.dispose();
            map?.removeLayer(layer);
        })
    }

    const measureOn = () => {
        if (!map) return;
        const measureLayer = getLayerByProps(map, LAYER_PROPS.LAYER_TYPE, LAYER.MEASURE_LAYER);
        if (measureLayer) {
            clearLayers(measureLayer);
        }
        const vectorSource = new VectorSource();
        const vectorLayer = new VectorLayer({
            source: vectorSource,
            style: (feature) => measureStyleFunction(feature, true)
        });

        vectorLayer.set(LAYER_PROPS.LAYER_TYPE, LAYER.MEASURE_LAYER);
        vectorLayer.setZIndex(LAYER_ZINDEX.MEASURE);

        map.addLayer(vectorLayer);
        const modify = new Modify({ source: vectorSource, style: MEASURE_MODIFY_STYLE, condition: primaryAction });
        modify.set(INTERACTION_PROPS.INTERACTION_TYPE, INTERACTION.MODIFY);
        map.addInteraction(modify);
    }

    const measureOff = () => {
        if (!map) return;
        const measureLayer = getLayerByProps(map, LAYER_PROPS.LAYER_TYPE, LAYER.MEASURE_LAYER);
        measureLayer.length > 0 && map.removeLayer(measureLayer[0]);
        map?.getOverlays().clear();
        clearMeasureInteractions();
    }

    const initMeasureInteraction = (drawType: 'Polygon' | 'LineString') => {
        const draw = getDraw();
        if (draw) {
            map?.removeInteraction(getDraw() as Draw);
        }
        addMeasureInteraction(drawType);
    }

    const addMeasureInteraction = (drawType: 'Polygon' | 'LineString') => {
        const idleTip = 'Click to start measuring';
        const activeTip = 'Click to continue drawing the ' + (drawType === 'Polygon' ? 'polygon' : 'line');
        const modify = getModify();
        if (!map) return;
        const measureLayer = getLayerByProps(map, LAYER_PROPS.LAYER_TYPE, LAYER.MEASURE_LAYER);
        if (measureLayer.length === 0) return;
        let tip = idleTip;

        const draw = new Draw({
            source: measureLayer[0].getSource() as VectorSource,
            type: drawType,
            style: (feature) => measureStyleFunction(feature, true),
            condition: primaryAction,
        });
        draw.set(INTERACTION_PROPS.INTERACTION_TYPE, INTERACTION.DRAW);
        draw.on('drawstart', () => {
            modify?.setActive(false);
            tip = activeTip;
        });
        draw.on('drawend', () => {
            if (measureTipPoint) {
                MEASURE_MODIFY_STYLE.setGeometry(measureTipPoint as Geometry);
            }
            modify?.setActive(true);
            map?.once('pointermove', () => MEASURE_MODIFY_STYLE.setGeometry(undefined as any));
            tip = idleTip;
        });
        modify?.setActive(true);
        map?.addInteraction(draw);
    }

    return {
        measureOn,
        measureOff,
        initMeasureInteraction,
        addMeasureInteraction,
        clearMeasureInteractions
    };
};
